import { call, put, select, takeEvery } from 'typed-redux-saga/macro';

import { ADD_WATERMARK, SET_WATERMARK_IMAGE_ASSET } from '../actions';
import { selectWatermarkRows, selectSession } from '../selectors';
import {
  serviceFindWatermarkImageAsset,
  serviceSaveWatermarkImageAsset,
} from '../services';
import { selectToken, selectGraphQLConfig } from './auth';
import { selectWatermarkProjectFor } from '@redact-modules/projects/renderedWatermarks';

/**
 * Store an image watermark's raster in its own asset when the mark is applied
 * (VE-24693 §J).
 *
 * As of asset format 1.3.0 an image mark persists an `imageAssetId` rather than
 * an inline base64 raster: the redaction-data body was unreadable with ~40k
 * characters of base64 in the middle of it, and that body gets opened by hand
 * whenever a redaction is disputed. So the artwork moved to its own
 * `document-watermark-image` asset and this saga is what puts it there.
 *
 * Written ONCE per (document, watermark), never rewritten. The asset holds only
 * the immutable raster; grayscale, tint, scale, opacity, rotation and placement
 * all stay in redaction-data, so adjusting a mark costs a JSON save and not an
 * upload. That also makes the id a durable snapshot: it resolves to exactly the
 * artwork that was applied, whatever later happens to the library definition —
 * including a permanent delete (AC#3).
 *
 * Reuse before upload: two instances of the same library watermark on one
 * document share a single asset, keyed on `watermarkId`. Applying the same
 * entry twice is a normal thing to do (two positions, two angles) and it should
 * not store the same bytes twice.
 *
 * Fire-and-forget with respect to the user: `ADD_WATERMARK` has already put the
 * mark on the canvas from the in-memory raster, so the upload never blocks the
 * editor. If it fails the mark stays visible and usable for the session and the
 * failure surfaces on save, which is the point at which it actually matters.
 */
function* onWatermarkAdded(
  action: ReturnType<typeof ADD_WATERMARK>
): Generator<unknown, void, unknown> {
  const added = action.payload;
  if (added.type !== 'image') {
    return;
  }
  // Already stored — a mark restored by an undo, or applied from the batch path
  // which uploads before it writes the body.
  if (added.imageAssetId) {
    return;
  }
  // Nothing to store. An image mark with no raster cannot be drawn either, so
  // this is a caller bug rather than a state to recover from.
  if (!added.imageBaseDataUrl) {
    return;
  }

  const session = yield* select(selectSession);
  if (!session) {
    return;
  }
  // An auto-applied mark's id is recorded as auto too, or the upload this saga
  // starts would dirty the document the ADD deliberately left clean (VE-30011).
  const meta = action.meta?.auto ? { auto: true } : undefined;

  // Share the asset with an instance of the same library entry that already has
  // one. `watermarkId` is the only thing that identifies "the same artwork"
  // here — `instanceId` is per-application by construction, and comparing the
  // rasters themselves would mean hashing megabytes to save kilobytes.
  const siblings = yield* select(selectWatermarkRows);
  const shared = added.watermarkId
    ? siblings.find((w) => {
        if (w.instanceId === added.instanceId || !w.imageAssetId) {
          return false;
        }
        if (w.type !== 'image') {
          return false;
        }
        return w.watermarkId === added.watermarkId;
      })
    : undefined;
  if (shared?.imageAssetId) {
    yield* put(
      SET_WATERMARK_IMAGE_ASSET(
        {
          instanceId: added.instanceId,
          imageAssetId: shared.imageAssetId,
          // The sibling's container, not a freshly resolved one: the asset is
          // wherever IT was written, and that is what a reader has to be told.
          containerId: shared.imageAssetContainerId ?? String(session.tdoId),
        },
        meta
      )
    );
    return;
  }

  const token: string = yield* select(selectToken);
  const { apiRoot, graphQLEndpoint, veritoneAppId } =
    yield* select(selectGraphQLConfig);

  /**
   * WHERE THE RASTER BELONGS (VE-30104).
   *
   * A set-wide mark is drawn by every member, and
   * `serviceLoadWatermarkImageAsset` checks the container and fails closed on
   * a mismatch — so a raster written to the member that happened to be open
   * loads for that one file and for no other. It goes on the PROJECT, which
   * is the one container all of them can reach.
   *
   * Undefined for a file no merged document governs, and also while the
   * membership has not been read: that is a window in which the panel would
   * not have offered "applies to all" either, so the mark is this document's
   * and its own TDO is right.
   */
  const projectTdoId = yield* select(selectWatermarkProjectFor(session.tdoId));
  // SCOPE, not just membership. A mark the reviewer scoped to this file
  // alone had its artwork written to the PROJECT container — which deleting
  // the merged document destroys, taking with it the artwork of marks that
  // were never part of what was deleted. A document's own data does not live
  // in another object's lifecycle.
  const container =
    action.meta?.fileOnly || !projectTdoId ? session.tdoId : projectTdoId;

  try {
    // Reuse before upload. The document may already hold this watermark's
    // raster from an earlier session — including one the user abandoned without
    // saving, or one whose mark they since removed. Finding it makes applying a
    // watermark idempotent, which is what lets this scheme delete nothing: an
    // "orphaned" asset is just the copy the next apply will pick up.
    const existing = yield* call(
      serviceFindWatermarkImageAsset,
      container,
      added.watermarkId,
      added.imageBaseDataUrl,
      token,
      apiRoot,
      graphQLEndpoint,
      veritoneAppId
    );
    const id =
      existing ??
      (yield* call(
        serviceSaveWatermarkImageAsset,
        container,
        added.imageBaseDataUrl,
        added.imageName,
        added.watermarkId,
        token,
        apiRoot,
        graphQLEndpoint
      )).id;
    yield* put(
      SET_WATERMARK_IMAGE_ASSET(
        {
          instanceId: added.instanceId,
          imageAssetId: id,
          containerId: String(container),
        },
        meta
      )
    );
  } catch (err) {
    // Deliberately not a snackbar. The mark is on the canvas and works for the
    // whole session; what is lost is only its persistence, and the save path is
    // where that becomes visible and actionable. Warning twice for one fault
    // trains people to ignore the warning.
    console.warn('Watermark raster could not be stored as an asset', err);
  }
}

export default function* watermarkImageAssetSaga() {
  yield* takeEvery(ADD_WATERMARK, onWatermarkAdded);
}
